import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LayoutDashboard, FileText, Users, ExternalLink, LogOut } from "lucide-react";
import AdminStatsCards from "@/components/AdminStatsCards";

type StatusCounts = { total: number; active: number; trial: number; trialEnded: number; blocked: number; pending: number };

const AdminDashboard = () => {
  const { signOut, user } = useAuth();
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [stats, setStats] = useState<StatusCounts>({ total: 0, active: 0, trial: 0, trialEnded: 0, blocked: 0, pending: 0 });

  useEffect(() => {
    const fetchData = async () => {
      const { data: apps, count, error } = await supabase
        .from("membership_applications")
        .select("id, status, email", { count: "exact" });

      if (!error && apps) {
        const pending = apps.filter(a => a.status === 'pending').length;
        setPendingCount(pending);

        try {
          const { data } = await supabase.functions.invoke('get-member-statuses');
          if (data?.members) {
            const members = data.members as Array<{ is_subscribed: boolean; trial_requests_used: number; trial_start_date: string | null; email: string }>;
            let active = 0, trial = 0, trialEnded = 0, blocked = 0;
            const memberMap = new Map(members.map(m => [m.email, m]));

            apps.forEach(app => {
              if (app.status === 'pending') return;
              if (app.status === 'blocked') { blocked++; return; }
              const m = memberMap.get((app as any).email);
              if (m?.is_subscribed) { active++; return; }
              const used = m?.trial_requests_used ?? 0;
              const start = m?.trial_start_date ? new Date(m.trial_start_date) : null;
              const days = start ? Math.floor((Date.now() - start.getTime()) / 86400000) : 0;
              if (used >= 10 || days >= 7) trialEnded++;
              else trial++;
            });
            setStats({ total: count ?? apps.length, active, trial, trialEnded, blocked, pending });
          } else {
            setStats({ total: count ?? apps.length, active: 0, trial: 0, trialEnded: 0, blocked: 0, pending });
          }
        } catch {
          setStats({ total: count ?? apps.length, active: 0, trial: 0, trialEnded: 0, blocked: 0, pending });
        }
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-primary" />
            <h1 className="text-lg font-bold">Admin Portal</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={signOut}>
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold mb-6">Dashboard</h2>
        <AdminStatsCards {...stats} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 max-w-3xl">
          <Link to="/admin/submissions" className="block p-6 bg-card border rounded-lg hover:border-primary transition-colors">
            <FileText className="w-8 h-8 text-primary mb-2" />
            <h3 className="font-semibold">Contact Submissions</h3>
            <p className="text-sm text-muted-foreground mt-1">View and manage form submissions</p>
          </Link>

          <Link to="/admin/membership-applications" className="block p-6 bg-card border rounded-lg hover:border-primary transition-colors relative">
            <Users className="w-8 h-8 text-primary mb-2" />
            <h3 className="font-semibold">Membership Applications</h3>
            <p className="text-sm text-muted-foreground mt-1">Review and manage applications</p>
            {pendingCount !== null && pendingCount > 0 && (
              <Badge className="absolute top-4 right-4">{pendingCount} pending</Badge>
            )}
          </Link>

          <a href="/member-register" target="_blank" rel="noopener noreferrer" className="block p-6 bg-card border rounded-lg hover:border-primary transition-colors">
            <ExternalLink className="w-8 h-8 text-primary mb-2" />
            <h3 className="font-semibold">Membership Sign Up Portal</h3>
            <p className="text-sm text-muted-foreground mt-1">Open the public registration page</p>
          </a>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
